import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Award, LogOut, Globe, User, Clock, Gift } from "lucide-react";
import type { TFunction } from 'i18next';

/**
 * GuestPortalHeader - Sticky top banner with branding, desktop tab navigation,
 * a de/en language toggle, loyalty points display, and logout control for the
 * guest portal.
 *
 * @param {Object} props - Component props.
 * @param {string} props.activeTab - Currently active tab key.
 * @param {Function} props.setActiveTab - Sets the active tab.
 * @param {number} props.loyaltyPoints - Server-truth loyalty balance (guests row).
 * @param {Function} props.handleLogout - Logout handler.
 * @param {Function} props.t - i18n translation function.
 * @returns {React.ReactElement} The rendered header.
 */
interface GuestPortalHeaderProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  loyaltyPoints: number;
  handleLogout: () => void;
  t: TFunction;
}

export default function GuestPortalHeader({ activeTab, setActiveTab, loyaltyPoints, handleLogout, t }: GuestPortalHeaderProps) {
  const { i18n } = useTranslation();

  const changeLanguage = (lang: "de" | "en") => {
    i18n.changeLanguage(lang); // persisted via the i18n languageChanged listener
  };

  return (
    <header className="sticky top-0 z-40 bg-surface border-b border-border px-4 md:px-8 py-4 flex items-center justify-between shadow-xs">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-bg border border-border flex items-center justify-center text-accent shadow-inner">
          <Sparkles size={20} className="animate-pulse" />
        </div>
        <div>
          <h1 className="text-sm font-black display-font tracking-wider uppercase">
            {t("guestPortal.title")}
          </h1>
          <p className="text-[8px] font-black text-accent uppercase tracking-widest">
            {t("guestPortal.brandSubtitle")}
          </p>
        </div>
      </div>

      {/* Tab Selection */}
      <nav className="hidden md:flex items-center gap-2">
        <button
          onClick={() => setActiveTab("dashboard")}
          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${activeTab === "dashboard" ? "bg-accent-muted text-accent" : "text-muted hover:bg-bg"
            }`}
        >
          {t("guestPortal.dashboardTab")}
        </button>
        <button
          onClick={() => setActiveTab("reservations")}
          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${activeTab === "reservations" ? "bg-accent-muted text-accent" : "text-muted hover:bg-bg"
            }`}
        >
          {t("guestPortal.myStays")}
        </button>
        <button
          onClick={() => setActiveTab("invoices")}
          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${activeTab === "invoices" ? "bg-accent-muted text-accent" : "text-muted hover:bg-bg"
            }`}
        >
          {t("guestPortal.invoices")}
        </button>
        <button
          onClick={() => setActiveTab("requests")}
          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all inline-flex items-center gap-1.5 ${activeTab === "requests" ? "bg-accent-muted text-accent" : "text-muted hover:bg-bg"
            }`}
        >
          <Clock size={12} /> {t("guestPortal.requestsTab")}
        </button>
        <button
          onClick={() => setActiveTab("rewards")}
          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all inline-flex items-center gap-1.5 ${activeTab === "rewards" ? "bg-accent-muted text-accent" : "text-muted hover:bg-bg"
            }`}
        >
          <Gift size={12} /> {t("guestPortal.rewardsTab")}
        </button>
        <button
          onClick={() => setActiveTab("profile")}
          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all inline-flex items-center gap-1.5 ${activeTab === "profile" ? "bg-accent-muted text-accent" : "text-muted hover:bg-bg"
            }`}
        >
          <User size={12} /> {t("guestPortal.profileTab")}
        </button>
      </nav>

      {/* Right Header Controls */}
      <div className="flex items-center gap-4">
        {/* DE/EN language toggle (guestPortal i18n keys, per i18n mandate) */}
        <div
          className="flex items-center gap-1 bg-bg border border-border rounded-full p-1"
          role="group"
          aria-label={t("guestPortal.languageLabel")}
        >
          <Globe size={13} className="text-muted ml-2 shrink-0" />
          {(["de", "en"] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => changeLanguage(lang)}
              aria-pressed={i18n.language === lang}
              className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors ${
                i18n.language === lang
                  ? "bg-accent text-white"
                  : "text-muted hover:text-fg"
              }`}
            >
              {t(`guestPortal.lang_${lang}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-xl shadow-inner text-indigo-600">
          <Award size={16} />
          {/* Note: the surrounding indigo tint is legacy kept for design parity;
              the value is the server-truth balance passed as a prop. */}
          <span className="text-xs font-bold">
            {t("guestPortal.pointsDisplay", { points: loyaltyPoints })}
          </span>
        </div>

        <button
          onClick={handleLogout}
          className="p-2 bg-rose-50 text-rose-500 hover:bg-rose-100 rounded-xl border border-rose-100 transition-colors"
          title={t("common.logout")}
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}
